import { useTranslation } from "react-i18next";

import { CHARACTER_CLASSES, systemLabelKey } from "../../catalogs/characterSystemCatalog";
import DraggableModal from "../layout/DraggableModal/DraggableModal";

const defaultClass = (excludedIds = []) => ({
  id: CHARACTER_CLASSES.find((option) => !excludedIds.includes(option.id))?.id || CHARACTER_CLASSES[0].id,
  classLevel: 1,
  availableWeapons: "",
  availableArmor: "",
});

const ClassManagerModal = ({ characterLevel, classes = [], isOpen, onChange, onClose }) => {
  const { t } = useTranslation();
  const normalizedClasses = classes.length > 0 ? classes : [defaultClass()];
  const isMulticlass = normalizedClasses.length > 1;
  const allocatedLevels = normalizedClasses.reduce(
    (total, entry) => total + (Number(entry.classLevel) || 0),
    0
  );
  const targetLevel = Number(characterLevel) || allocatedLevels;
  const hasLevelMismatch = isMulticlass && allocatedLevels !== targetLevel;

  const updateClass = (index, changes) => {
    onChange(normalizedClasses.map((entry, entryIndex) => (
      entryIndex === index ? { ...entry, ...changes } : entry
    )));
  };

  const setMulticlass = (enabled) => {
    if (!enabled) {
      onChange([{ ...normalizedClasses[0], classLevel: targetLevel }]);
      return;
    }

    if (!isMulticlass) {
      onChange([
        {
          ...normalizedClasses[0],
          classLevel: Math.max(0, (Number(normalizedClasses[0].classLevel) || 0) - 1),
        },
        { ...defaultClass(normalizedClasses.map((entry) => entry.id)), classLevel: 1 },
      ]);
    }
  };

  const addClass = () => {
    const usedIds = normalizedClasses.map((entry) => entry.id);
    if (usedIds.length >= CHARACTER_CLASSES.length) return;
    onChange([...normalizedClasses, defaultClass(usedIds)]);
  };

  return (
    <DraggableModal
      className="class-manager-modal"
      headerText={t("character.classSettings")}
      isVisible={isOpen}
      onClose={onClose}
    >
      <label className="class-manager-toggle">
        <input
          checked={isMulticlass}
          type="checkbox"
          onChange={(event) => setMulticlass(event.target.checked)}
        />
        <span>{t("character.enableMulticlass")}</span>
      </label>

      {isMulticlass && (
        <p className={`class-level-allocation${hasLevelMismatch ? " class-level-allocation-warning" : ""}`} role="status">
          {t("character.allocatedClassLevels", { allocated: allocatedLevels, total: targetLevel })}
        </p>
      )}

      <div className="class-manager-list">
        {normalizedClasses.map((entry, index) => (
          <div className="class-manager-row" key={`${entry.id}-${index}`}>
            <label>
              <span>{index === 0 ? t("character.primaryClass") : t("character.additionalClass")}</span>
              <select
                aria-label={index === 0 ? t("character.primaryClass") : t("character.additionalClassNumber", { number: index })}
                value={entry.id}
                onChange={(event) => updateClass(index, { id: event.target.value })}
              >
                {CHARACTER_CLASSES.map((option) => {
                  const usedByAnotherRow = normalizedClasses.some(
                    (selected, selectedIndex) => selectedIndex !== index && selected.id === option.id
                  );
                  return (
                    <option disabled={usedByAnotherRow} key={option.id} value={option.id}>
                      {t(systemLabelKey("classes", option.id))}
                    </option>
                  );
                })}
              </select>
            </label>

            <label className="class-manager-level">
              <span>{t("character.classLevel")}</span>
              <input
                aria-label={t("character.classLevelNumber", { number: index + 1 })}
                min="0"
                type="number"
                value={Number(entry.classLevel) || 0}
                onChange={(event) => updateClass(index, {
                  classLevel: Math.max(0, Number(event.target.value) || 0),
                })}
              />
            </label>

            {index > 0 && (
              <button
                aria-label={t("character.removeClass", { name: t(systemLabelKey("classes", entry.id)) })}
                className="class-manager-remove"
                type="button"
                onClick={() => onChange(normalizedClasses.filter((_, entryIndex) => entryIndex !== index))}
              >
                &times;
              </button>
            )}
          </div>
        ))}
      </div>

      {isMulticlass && normalizedClasses.length < CHARACTER_CLASSES.length && (
        <button className="class-manager-add" type="button" onClick={addClass}>
          + {t("character.addClass")}
        </button>
      )}
    </DraggableModal>
  );
};

export default ClassManagerModal;
